import { css } from "@linaria/core"
import { Box, Link, Stack, Typography } from "@mui/material"
import { SwitchVertical01 } from "@untitled-ui/icons-react"
import React, { useEffect, useLayoutEffect, useMemo } from "react"

import { Reserves } from "../../../ao/pool"
import { TokenInfo } from "../../../ao/token"
import { COMMON_QUOTE_TOKENS, INPUT_DECIMALS } from "../../../settings"
import {
  decimalEnforcer,
  formatNumberAuto,
  formatTicker,
  truncateNumberAuto,
} from "../../../utils/format"
import { IconButton } from "../../IconButton"
import Skeleton from "../../Skeleton"
import { Input, SmallButton } from "../../TokenInput"
import TokenLogo from "../../TokenLogo"
import TokenPill from "../../TokenPill"

const _1_HOUR = 60
const _4_HOURS = 4 * 60
const _1_DAY = 24 * 60
const _3_DAYS = 3 * 24 * 60

export type LimitOrderFormValues = {
  limitPrice?: string
  /**
   * Expiry in minutes
   */
  expiry?: number
}

type LimitOrderFormProps = {
  fromToken?: TokenInfo
  toToken?: TokenInfo
  reserves?: Reserves
  onValuesChange?: (values: LimitOrderFormValues) => void
  poolId?: string
}

export function LimitOrderForm(props: LimitOrderFormProps) {
  const { poolId, reserves, onValuesChange } = props

  const [limitPrice, setLimitPrice] = React.useState<string>("")
  const [reversed, setReversed] = React.useState<boolean>(false)

  useLayoutEffect(() => {
    if (Boolean(props.toToken && COMMON_QUOTE_TOKENS.includes(props.toToken?.id))) {
      setReversed(false)
    } else {
      setReversed(true)
    }
  }, [props.fromToken, props.toToken])

  // in minutes
  const [expiry, setExpiry] = React.useState<number>(_3_DAYS)

  const fromToken = reversed ? props.toToken : props.fromToken
  const toToken = reversed ? props.fromToken : props.toToken

  // market price
  const price = useMemo(() => {
    if (!reserves || !fromToken || !toToken) return null

    const base = reserves[fromToken.id]
    const baseToken = fromToken
    const quote = reserves[toToken.id]
    const quoteToken = toToken

    let price =
      quote === 0n
        ? null
        : Number(quote) /
          10 ** quoteToken.denomination /
          (Number(base) / 10 ** baseToken.denomination)

    return price
  }, [fromToken, toToken, reserves])

  const [pristine, setPristine] = React.useState(true)

  useEffect(() => {
    setPristine(true)
  }, [props.fromToken, props.toToken])

  useEffect(() => {
    if (price !== null && !isNaN(price) && pristine) {
      setLimitPrice(truncateNumberAuto(price))
    }
  }, [pristine, price])

  useEffect(() => {
    if (!onValuesChange) return

    if (!reversed) {
      onValuesChange({ expiry, limitPrice })
      return
    }

    const reversedInput = 1 / Number(limitPrice)
    if (reversedInput === Infinity) return
    onValuesChange({ expiry, limitPrice: limitPrice ? String(reversedInput) : "" })
  }, [reversed, limitPrice, onValuesChange, expiry])

  return (
    <>
      <Stack
        sx={{
          background: "var(--mui-palette-background-default)",
          paddingX: 1.25,
          paddingY: 1,
          borderRadius: 1,
          order: -1,
        }}
        gap={1}
      >
        <Typography color="secondary" component="div">
          {fromToken && toToken ? (
            <Stack direction="row" justifyContent="space-between">
              <Stack direction="row" gap={1}>
                When one
                <TokenPill>
                  <TokenLogo tx={fromToken?.logo} alt={`${fromToken?.name} logo`} size="small" />
                  {formatTicker(fromToken?.ticker)}{" "}
                </TokenPill>{" "}
                is worth:
              </Stack>
              <IconButton
                size="small"
                onClick={() => {
                  setReversed((prev) => !prev)
                  const reversedInput = String(1 / Number(limitPrice))
                  if (reversedInput === "Infinity") return
                  setLimitPrice(truncateNumberAuto(reversedInput))
                }}
              >
                <SwitchVertical01 />
              </IconButton>
            </Stack>
          ) : (
            "Limit price"
          )}
        </Typography>
        <Stack direction="row" gap={1} alignItems="center">
          <Input
            name="limitPrice"
            className={css`
              font-size: var(--xl-font-size);
              width: 320px;
              flex: 1;
            `}
            type="text"
            placeholder="0"
            value={limitPrice}
            onChange={(event) => {
              const nextInput = decimalEnforcer(event.target.value.replace(/,/g, "."))?.replace(
                /^0+$/,
                "0",
              )
              if (nextInput !== undefined) {
                setLimitPrice(nextInput)
                setPristine(false)
              }
            }}
            pattern="^[0-9]*[.,]?[0-9]*$"
          />
          {fromToken && toToken && (
            <TokenPill
              className={css`
                font-size: 1.5rem;
              `}
            >
              <TokenLogo tx={toToken.logo} alt={`${toToken.name} logo`} size="medium" />
              {formatTicker(toToken.ticker)}
            </TokenPill>
          )}
        </Stack>
        <Box sx={{ minHeight: 25 }}>
          {fromToken && toToken && !reserves && <Skeleton height="1.25rem" width="60px" />}
          {fromToken && toToken && reserves && !!price && (
            <Typography
              variant="caption"
              sx={{
                background: "var(--mui-palette-background-paper)",
                borderRadius: 5,
                paddingY: 0.25,
                paddingX: 0.75,
              }}
              color="secondary"
            >
              {formatNumberAuto(price, INPUT_DECIMALS)} {toToken?.ticker} = Current Price
            </Typography>
          )}
        </Box>
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{
            flexDirection: { xs: "column", sm: "row" },
          }}
        >
          <Stack
            direction="row"
            gap={1}
            alignItems="center"
            sx={{
              marginLeft: -0.5,
              height: 32,
            }}
          >
            {!!price && (
              <>
                <SmallButton
                  onClick={() => {
                    const nextInput = String(price)
                    setLimitPrice(truncateNumberAuto(nextInput))
                    setPristine(false)
                  }}
                >
                  Market
                </SmallButton>
                <SmallButton
                  onClick={() => {
                    const nextInput = reversed ? price * 0.99 : price * 1.01
                    setLimitPrice(truncateNumberAuto(nextInput))
                    setPristine(false)
                  }}
                >
                  {reversed ? "-" : "+"}1%
                </SmallButton>
                <SmallButton
                  onClick={() => {
                    const nextInput = reversed ? price * 0.95 : price * 1.05
                    setLimitPrice(truncateNumberAuto(nextInput))
                    setPristine(false)
                  }}
                >
                  {reversed ? "-" : "+"}5%
                </SmallButton>
                <SmallButton
                  onClick={() => {
                    const nextInput = reversed ? price * 0.9 : price * 1.1
                    setLimitPrice(truncateNumberAuto(nextInput))
                    setPristine(false)
                  }}
                >
                  {reversed ? "-" : "+"}10%
                </SmallButton>
              </>
            )}
          </Stack>
          {poolId && (
            <Link
              href={`https://dexi.arweave.net/#/pool/${poolId}`}
              target="_blank"
              rel="noopener noreferrer"
              underline="none"
              sx={{
                gap: 0.5,
                display: "flex",
                alignItems: "center",
                flexDirection: "row",
                background: "var(--mui-palette-background-paper)",
                paddingX: 1,
                borderRadius: 0.75,
                marginRight: -0.5,

                "& svg": {
                  width: "1rem",
                  height: "1rem",
                },
              }}
              color="secondary"
            >
              View Historical Prices on Dexi
              {/* <Share05 /> */}
            </Link>
          )}
        </Stack>
      </Stack>
      <Stack
        gap={1}
        direction="row"
        justifyContent="space-between"
        sx={{
          minHeight: 30,
          marginLeft: 1,
        }}
        alignItems="center"
      >
        <Typography variant="body2" color="text.secondary">
          Expiry
        </Typography>
        <Stack
          direction="row"
          sx={{
            marginBottom: -0.25,
            "& button": {
              borderRadius: "3px !important",
            },
            "& button:not(.active)": {
              background: "none",
            },
          }}
        >
          <SmallButton
            className={expiry === _1_HOUR ? "active" : ""}
            onClick={() => {
              setExpiry(_1_HOUR)
            }}
          >
            1 HOUR
          </SmallButton>
          <SmallButton
            className={expiry === _4_HOURS ? "active" : ""}
            onClick={() => {
              setExpiry(_4_HOURS)
            }}
          >
            4 HOURS
          </SmallButton>
          <SmallButton
            className={expiry === _1_DAY ? "active" : ""}
            onClick={() => {
              setExpiry(_1_DAY)
            }}
          >
            1 DAY
          </SmallButton>
          <SmallButton
            className={expiry === _3_DAYS ? "active" : ""}
            onClick={() => {
              setExpiry(_3_DAYS)
            }}
          >
            3 DAYS
          </SmallButton>
        </Stack>
      </Stack>
      {/* <Stack direction="row" alignItems="center" gap={0.5} padding={1}>
        <Typography variant="body2" color="text.secondary">
          Stop Loss
        </Typography>
        <AntSwitch color="primary" />
      </Stack> */}
    </>
  )
}
